import type { EmotionNodeData } from "./EmotionNodes";

/**
 * Node positions and links for the Emotions section, read off the Figma
 * frames (desktop 3165:28569 at 1920x646, mobile 3205:60992 at 382x600).
 * Coordinates are dot centers as a percentage of the graph box.
 *
 * Descriptions are placeholder copy: Figma shows the labels only.
 */

const DESCRIPTIONS: Record<string, string> = {
  execution:
    "Hesitation, early exits and chased entries all show up as the gap between your plan and your fills. Track it and the gap shrinks.",
  "decision-bias":
    "Recency, anchoring and confirmation quietly bend every read of the chart. Logging the reason behind each trade exposes the pattern.",
  confidence:
    "Confidence swings with the last result, not the quality of the setup. Journaling separates the two so you size with intent.",
  "emotional-patterns":
    "Tilt after a loss, euphoria after a win: the same feelings return on the same days. Seeing the cycle is the first step to breaking it.",
  "timing-errors":
    "Entries a candle too early, exits a candle too late. Tagging timing on every trade shows where the leak actually is.",
  pressure:
    "Drawdown, targets and other people's results push you into trades you never planned. Pressure leaves a trail in your journal.",
  "mistake-frequency":
    "One mistake is noise, the same mistake every week is a habit. Counting them turns vague regret into a fixable number.",
  "confidence-distortion":
    "A streak makes you feel unbeatable, a loss makes you doubt a valid edge. Neither is true, and your stats can prove it.",
  "per-drivers":
    "Sleep, stress, routine and mood drive performance more than any indicator. Tracking them links your life to your P&L.",
};

const LABELS: Record<string, string> = {
  execution: "Execution",
  "decision-bias": "Decision bias",
  confidence: "Confidence",
  "emotional-patterns": "Emotional patterns",
  "timing-errors": "Timing errors",
  pressure: "Pressure",
  "mistake-frequency": "Mistake frequency",
  "confidence-distortion": "Confidence distortion",
  "per-drivers": "Per. drivers",
};

type Layout = Record<string, { x: number; y: number; connections: string[] }>;

const DESKTOP: Layout = {
  human: { x: 46.77, y: 53.49, connections: ["execution", "decision-bias", "emotional-patterns", "confidence", "timing-errors", "per-drivers"] },
  execution: { x: 17.52, y: 23.31, connections: ["decision-bias", "confidence", "human"] },
  "decision-bias": { x: 37.36, y: 6.28, connections: ["execution", "emotional-patterns", "confidence", "human", "confidence-distortion"] },
  "emotional-patterns": { x: 58.06, y: 2.87, connections: ["decision-bias", "confidence", "human", "per-drivers"] },
  confidence: { x: 66.84, y: 28.88, connections: ["execution", "decision-bias", "emotional-patterns", "mistake-frequency", "human", "timing-errors", "pressure"] },
  "mistake-frequency": { x: 84.91, y: 42.19, connections: ["confidence", "timing-errors", "pressure"] },
  "timing-errors": { x: 70.17, y: 68.82, connections: ["confidence", "mistake-frequency", "human"] },
  pressure: { x: 52.49, y: 89.87, connections: ["confidence", "mistake-frequency", "per-drivers", "confidence-distortion"] },
  "confidence-distortion": { x: 16.5, y: 79.34, connections: ["decision-bias", "per-drivers", "pressure"] },
  "per-drivers": { x: 31.87, y: 53.95, connections: ["emotional-patterns", "human", "pressure", "confidence-distortion"] },
};

const MOBILE: Layout = {
  human: {
    x: 50,
    y: 44.67,
    connections: ["execution", "decision-bias", "confidence", "emotional-patterns", "timing-errors", "pressure", "mistake-frequency", "confidence-distortion", "per-drivers"],
  },
  execution: { x: 30.1, y: 2.5, connections: ["emotional-patterns", "confidence", "decision-bias", "human"] },
  "decision-bias": { x: 74.08, y: 9, connections: ["execution", "emotional-patterns", "human"] },
  confidence: { x: 18.06, y: 21, connections: ["execution", "pressure", "timing-errors", "human"] },
  "emotional-patterns": { x: 77.62, y: 28.5, connections: ["execution", "decision-bias", "pressure", "human"] },
  "timing-errors": { x: 21.99, y: 58.17, connections: ["confidence-distortion", "confidence", "mistake-frequency", "human"] },
  pressure: { x: 77.88, y: 54.33, connections: ["confidence", "emotional-patterns", "confidence-distortion", "human"] },
  "mistake-frequency": { x: 30.1, y: 80.33, connections: ["timing-errors", "per-drivers", "human"] },
  "confidence-distortion": { x: 72.12, y: 74.83, connections: ["timing-errors", "per-drivers", "pressure", "human"] },
  "per-drivers": { x: 50.13, y: 90.5, connections: ["mistake-frequency", "confidence-distortion", "human"] },
};

function build(layout: Layout): EmotionNodeData[] {
  return Object.entries(layout).map(([id, node]) => ({
    id,
    label: id === "human" ? "You" : LABELS[id],
    x: node.x,
    y: node.y,
    connections: node.connections,
    description: id === "human" ? "" : DESCRIPTIONS[id],
  }));
}

export const EMOTION_NODES_DESKTOP = build(DESKTOP);
export const EMOTION_NODES_MOBILE = build(MOBILE);
export const EMOTION_GRAPH_ASPECT_DESKTOP = "1920 / 646";
export const EMOTION_GRAPH_ASPECT_MOBILE = "382 / 600";
